import type { DraftField } from "@sga/shared";

/**
 * Putting fields side by side — the storage is `FormField.rowGroup`, but the coordinator's intent
 * is "this field shares a row with the one above it", and the two are not the same thing.
 *
 * The renderer groups *consecutive fields carrying the same non-zero rowGroup* (see
 * packages/shared/src/form-renderer/FormRenderer.tsx). The builder's old menu item said "Place side
 * by side with previous" but only ever set the clicked field's rowGroup to a constant 1 — which
 * does nothing on its own, because the field above it still carries 0 and so still starts its own
 * row. A coordinator had to find the same buried menu item on both fields, and pick the same value,
 * to get one row. That is why the form the platform ships with is twelve full-width fields.
 *
 * These helpers work in intent instead, and own every rowGroup write in the builder.
 */

const sectionOf = (field: DraftField): string | null => field.sectionKey ?? null;

/** A section's fields in the order they render, which is the order "previous" refers to. */
const siblingsOf = (fields: readonly DraftField[], field: DraftField): DraftField[] =>
  fields
    .filter((candidate) => sectionOf(candidate) === sectionOf(field))
    .slice()
    .sort((a, b) => a.position - b.position);

const findField = (fields: readonly DraftField[], key: string): DraftField | undefined =>
  fields.find((field) => field.key === key);

/** The field a "join the row above" would attach to, or undefined if this one is first. */
export const previousSibling = (
  fields: readonly DraftField[],
  key: string,
): DraftField | undefined => {
  const field = findField(fields, key);
  if (!field) return undefined;
  const siblings = siblingsOf(fields, field);
  const index = siblings.findIndex((candidate) => candidate.key === key);
  return index > 0 ? siblings[index - 1] : undefined;
};

export const canJoinPreviousRow = (fields: readonly DraftField[], key: string): boolean =>
  previousSibling(fields, key) !== undefined;

/** Everyone currently sharing a row with `key`, `key` included. */
export const rowMembers = (fields: readonly DraftField[], key: string): DraftField[] => {
  const field = findField(fields, key);
  if (!field || field.rowGroup === 0) return field ? [field] : [];
  return siblingsOf(fields, field).filter((candidate) => candidate.rowGroup === field.rowGroup);
};

const nextGroupId = (fields: readonly DraftField[]): number =>
  Math.max(0, ...fields.map((field) => field.rowGroup)) + 1;

/**
 * Attach a field to the row above it — assigning *both* fields the same group, since the one above
 * is usually still on its own row and a group of one renders no differently from no group at all.
 */
export const joinPreviousRow = (fields: DraftField[], key: string): DraftField[] => {
  const previous = previousSibling(fields, key);
  if (!previous) return fields;

  const group = previous.rowGroup !== 0 ? previous.rowGroup : nextGroupId(fields);

  return fields.map((field) =>
    field.key === key || field.key === previous.key ? { ...field, rowGroup: group } : field,
  );
};

/**
 * Give a field its own row again.
 *
 * If that leaves a single field behind holding the old group, it is reset to 0 as well: a one-field
 * row is indistinguishable on screen, but a stray non-zero group would silently swallow the *next*
 * field the coordinator joins to a row, which reads as the builder rearranging itself unprompted.
 */
export const splitFromRow = (fields: DraftField[], key: string): DraftField[] => {
  const field = findField(fields, key);
  if (!field || field.rowGroup === 0) return fields;

  const orphans = rowMembers(fields, key)
    .filter((member) => member.key !== key)
    .map((member) => member.key);
  const stranded = orphans.length === 1 ? orphans[0] : undefined;

  return fields.map((candidate) =>
    candidate.key === key || candidate.key === stranded ? { ...candidate, rowGroup: 0 } : candidate,
  );
};
